import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { getGroupById, createGroup, checkGroupNameExists } from '../api/groupAPI';
import { getPaginatedGlobalGroupsCombined, getGlobalGroupById } from '../api/globalGroupAPI';
import { getAssetType } from '../api/assetTypeAPI';
import { getPlanningType } from '../api/planning-typeAPI';
import { createAsset, getAssetBySerial } from '../api/assetAPI';
import { clearCache } from '../utils/lookupCache';
// import { createAssetFinancial } from '../api/asset-financialAPI';
import { createAssetMeter } from '../api/asset-meterAPI';
import { createMeterReadings } from '../api/meter-readingsAPI';
import { getMeters } from '../api/metersAPI';
import { getHierarchyStructures } from '../api/hierarchy-structureAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { validateForm } from '../utils/useValidator';
import { useValidator } from '../utils/useValidator';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useSetting } from '../utils/AppSettingsContext';
import { createCostCentre, getCostCentre, checkCostCentreDuplicate } from '../api/cost-centreAPI';
import { calculateMeterEndOfLife } from '../utils/meterUtils';

const ADD_COST_CENTRE_VALUE = 'add-cost-centre';

const AddAssetWizard = ({ show, onClose, onSaved }) => {
   const todayDate = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  const [currentStep, setCurrentStep] = useState(1);
  const [newAsset, setNewAsset] = useState({});
  const [errors, setErrors] = useState({});
  const [assetTypes, setAssetTypes] = useState([]);
  const [planningTypes, setPlanningTypes] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [newGroup, setNewGroup] = useState({});
  const [groupErrors, setGroupErrors] = useState({});
  const [serialRequired, setSerialRequired] = useState(false);
  const [meterList, setMeterList] = useState([]);
  const [addedMeters, setAddedMeters] = useState([]);
  const [showAddMeterModal, setShowAddMeterModal] = useState(false);
  const [newMeterId, setNewMeterId] = useState(null);
  const [newStartReading, setNewStartReading] = useState('0');
  const [newUtilisation, setNewUtilisation] = useState('');
  const [newPrimaryMeter, setNewPrimaryMeter] = useState(false);
  const [utilisationError, setUtilisationError] = useState('');
  const [defaultMeterId, setDefaultMeterId] = useState(null);
  const [defaultUtilisation, setDefaultUtilisation] = useState(null);
  const [editingMeterIndex, setEditingMeterIndex] = useState(null);
  const [costCentreOptions, setCostCentreOptions] = useState([
    { value: ADD_COST_CENTRE_VALUE, label: '+ Add Cost Centre' },
  ]);
  const [showAddCostCentreModal, setShowAddCostCentreModal] = useState(false);
  const [newCostCentre, setNewCostCentre] = useState({});
  const [costCentreErrors, setCostCentreErrors] = useState({});
  const userProfile = useUserProfile();
  const defaultCostCentre = useSetting('default_cost_centre');
  const showAssetCategory = useSetting('show_asset_category');
  const defaultCostCentreId =
    defaultCostCentre !== null && defaultCostCentre !== undefined
      ? Number(defaultCostCentre) || null
      : null;

  const meterFormData = useMemo(
    () => ({ meterId: newMeterId, utilisation: newUtilisation }),
    [newMeterId, newUtilisation],
  );

  const meterValidationFields = useMemo(
    () => [
      [{ field: 'meterId', label: 'Meter', required: true }],
      [
        {
          field: 'utilisation',
          label: 'Utilisation per Year',
          type: 'number',
          required: true,
          mustBeGreaterThanZero: true,
        },
      ],
    ],
    [],
  );

  const {
    errors: addMeterErrors,
    validate: validateMeterForm,
    resetErrors: resetMeterValidationErrors,
  } = useValidator(meterValidationFields, meterFormData);

  const resetMeterForm = useCallback(() => {
    setNewMeterId(null);
    setNewStartReading('0');
    setNewUtilisation('');
    setNewPrimaryMeter(false);
    setUtilisationError('');
    setEditingMeterIndex(null);
    resetMeterValidationErrors();
  }, [
    resetMeterValidationErrors,
  ]);

  const prevAssetTypeId = useRef();

  const groupInputFields = useMemo(
    () => [
      { field: 'groupName', label: 'Model or Group Name', type: 'text', size: 6, required: true },
      { field: 'imageBlock1', label: 'Image', type: 'emptyBorder', size: 6, required: false },
      { field: 'classId', label: 'Class', type: 'select', size: 6, api: 'class', displayField: 'className' },
      { field: 'generalSpacer1', label: '', type: 'empty', size: 6, required: false },
      {
        field: 'manufacturerId',
        label: 'Manufacturer',
        type: 'select',
        size: 6,
        api: 'manufacturers',
        displayField: 'manufacturer',
      },
      { field: 'generalSpacer2', label: '', type: 'empty', size: 6, required: false },
      { field: 'groupImage', label: 'Group Image', type: 'image', size: 6, directory: 'modelimages' },
      { field: 'generalSpacer3', label: '', type: 'empty', size: 6, required: false },
      { field: 'groupDescription', label: 'Description', type: 'textarea', size: 6 },
    ],
    []
  );

  const { renderField: renderGroupField } = useFormBuilder(
    groupInputFields,
    newGroup,
    setNewGroup,
    groupErrors
  );

  const runGroupValidation = () => {
    const newErrors = validateForm(groupInputFields, newGroup);
    setGroupErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGroupSave = async () => {
    if (!runGroupValidation()) return;
    try {
      const { exists } = await checkGroupNameExists({
        groupName: newGroup.groupName,
      });
      if (exists) {
        setGroupErrors((prev) => ({
          ...prev,
          groupName: 'Group name already exists',
        }));
        return;
      }

      const cleanPayload = {};
      for (const key in newGroup) {
        if (newGroup[key] !== null && typeof newGroup[key] !== 'object') {
          cleanPayload[key] = newGroup[key];
        }
      }
      const created = await createGroup(cleanPayload);
      const grp = await getGroupById(created.id);
      const option = {
        value: grp.id,
        label: `${grp.manufacturer?.manufacturer || ''} ${grp.groupName || ''} ${grp.class?.className || ''
          }`,
        isLocal: true,
      };
      setSelectedGroup(option);
      setNewAsset((prev) => ({
        ...prev,
        groupId: grp.id,
        assetDescription: `${grp.manufacturer?.manufacturer || ''} ${grp.groupName || ''
          } ${grp.class?.className || ''}`.trim(),
        assetImage: grp.groupImage || prev.assetImage,
        ...(showAssetCategory && {
          assetCategoryId: grp.class?.categoryId ?? null,
        }),
      }));
      setShowAddGroupModal(false);
      setNewGroup({});
      setGroupErrors({});
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  const handleGroupChange = useCallback(
    (opt, setFormData) => {
      if (opt?.value === 'add-new') {
        setShowAddGroupModal(true);
        setSelectedGroup(null);
        setFormData((prev) => ({
          ...prev,
          groupId: null,
          ...(showAssetCategory && { assetCategoryId: null }),
        }));
      } else {
        setSelectedGroup(opt);
        setFormData((prev) => ({
          ...prev,
          groupId: opt?.value || null,
          ...(showAssetCategory && { assetCategoryId: null }),
        }));
      }
    },
    [showAssetCategory]
  );

  const formatCostCentreOption = useCallback((centre) => {
    if (!centre) return null;
    const code = centre.costCentreCode ? String(centre.costCentreCode).trim() : '';
    const description = centre.costCenterDescription ? String(centre.costCenterDescription).trim() : '';
    const label = [code, description].filter(Boolean).join(code && description ? ' - ' : '');
    return {
      value: centre.id,
      label: label || description || code || '',
    };
  }, []);

  const loadCostCentres = useCallback(async () => {
    try {
      const data = await getCostCentre();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const formatted = list
        .map(formatCostCentreOption)
        .filter(Boolean)
        .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
      setCostCentreOptions([
        { value: ADD_COST_CENTRE_VALUE, label: '+ Add Cost Centre' },
        ...formatted,
      ]);
    } catch (error) {
      console.error('Error loading cost centres:', error);
      setCostCentreOptions([{ value: ADD_COST_CENTRE_VALUE, label: '+ Add Cost Centre' }]);
    }
  }, [formatCostCentreOption]);

  useEffect(() => {
    loadCostCentres();
  }, [loadCostCentres]);

  const costCentreFields = useMemo(
    () => [
      { field: 'costCentreCode', label: 'Cost Centre Code', type: 'text', size: 6, required: true },
      { field: 'costCenterDescription', label: 'Description', type: 'text', size: 6, required: true },
    ],
    []
  );

  const { renderField: renderCostCentreField } = useFormBuilder(
    costCentreFields,
    newCostCentre,
    setNewCostCentre,
    costCentreErrors
  );

  const runCostCentreValidation = useCallback(() => {
    const newErrors = validateForm(costCentreFields, newCostCentre);
    setCostCentreErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [costCentreFields, newCostCentre, setCostCentreErrors]);

  const handleCostCentreSave = useCallback(async () => {
    if (!runCostCentreValidation()) return;
    try {
      const dupResult = await checkCostCentreDuplicate(
        newCostCentre.costCentreCode,
        newCostCentre.costCenterDescription,
        null
      );
      const duplicateErrors = {};
      if (dupResult?.codeExists) {
        duplicateErrors.costCentreCode = 'Code already exists';
      }
      if (dupResult?.nameExists) {
        duplicateErrors.costCenterDescription = 'Description already exists';
      }
      if (Object.keys(duplicateErrors).length > 0) {
        setCostCentreErrors((prev) => ({ ...prev, ...duplicateErrors }));
        return;
      }

      const payload = {};
      for (const key in newCostCentre) {
        if (newCostCentre[key] !== null && typeof newCostCentre[key] !== 'object') {
          payload[key] = newCostCentre[key];
        }
      }

      const created = await createCostCentre(payload);
      clearCache('cost-centres');
      const option = formatCostCentreOption(created);
      setCostCentreOptions((prev) => {
        const addOption = { value: ADD_COST_CENTRE_VALUE, label: '+ Add Cost Centre' };
        const withoutAdd = Array.isArray(prev)
          ? prev.filter((opt) => opt && opt.value !== ADD_COST_CENTRE_VALUE && opt.value !== option?.value)
          : [];
        return [addOption, ...(option ? [option] : []), ...withoutAdd];
      });
      setNewAsset((prev) => ({ ...prev, costCenterId: created?.id ?? null }));
      await loadCostCentres();
      setShowAddCostCentreModal(false);
      setNewCostCentre({});
      setCostCentreErrors({});
    } catch (error) {
      console.error('Error creating cost centre:', error);
    }
  }, [
    runCostCentreValidation,
    newCostCentre,
    formatCostCentreOption,
    loadCostCentres,
    setCostCentreErrors,
    setCostCentreOptions,
    setNewAsset,
    setShowAddCostCentreModal,
    setNewCostCentre,
  ]);

  const handleCostCentreChange = useCallback(
    (opt, setFormData) => {
      if (opt?.value === ADD_COST_CENTRE_VALUE) {
        setShowAddCostCentreModal(true);
        setNewCostCentre({});
        setCostCentreErrors({});
        setFormData((prev) => ({ ...prev, costCenterId: null }));
        return;
      }
      setFormData((prev) => ({ ...prev, costCenterId: opt?.value ?? null }));
    },
    [setShowAddCostCentreModal, setNewCostCentre, setCostCentreErrors]
  );

  useEffect(() => {
    const div = document.getElementById('imageBlock1');
    if (!div) return;
    if (showAddGroupModal && newGroup.groupImage) {
      div.style.backgroundImage = `url(${process.env.REACT_APP_SPACES_BASE}/${newGroup.groupImage})`;
      div.style.backgroundSize = 'cover';
      div.style.backgroundPosition = 'center';
    } else {
      div.style.backgroundImage = '';
    }
  }, [showAddGroupModal, newGroup.groupImage]);

  useEffect(() => {
    if (show) {
      const defaultStartDate = new Date().toISOString().split('T')[0];
      setNewAsset({
        plantStatusId: 6,
        assetStartDate: defaultStartDate,
        costCenterId: defaultCostCentreId,
        assetCriticalityId: 2,
        assetFiles: [],
        depnType: 'none',
        leaseType: 'none',
        leasePayments: 0,
        leaseInterestRate: 0,
        leaseStartDate: defaultStartDate,
      });
      setErrors({});
      setLifeUtilisationErrors({});
      setCurrentStep(1);
      setAddedMeters([]);
      setDefaultMeterId(null);
      setDefaultUtilisation(null);
      resetMeterForm();
      setShowAddMeterModal(false);
      getMeters().then(setMeterList).catch(() => { });
      getHierarchyStructures()
        .then((deps) => {
          if (Array.isArray(deps) && deps.length === 1) {
            setNewAsset((prev) => ({ ...prev, assetDepartmentId: deps[0].id }));
          }
        })
        .catch(() => { });
    }
  }, [show, defaultCostCentreId, userProfile, resetMeterForm]);

  useEffect(() => {
    getAssetType().then(setAssetTypes).catch(() => { });
    getPlanningType().then(setPlanningTypes).catch(() => { });
  }, []);

  useEffect(() => {
    const selected = assetTypes.find((t) => t.id === newAsset.AssetTypeId);
    if (selected) {
      setSerialRequired(selected.mandatorySerialNumber === 1);
      if (
        selected.defaultPlanningType &&
        prevAssetTypeId.current !== newAsset.AssetTypeId
      ) {
        setNewAsset((prev) => ({
          ...prev,
          PlanningTypeId: selected.defaultPlanningType,
        }));
      }
      setDefaultMeterId(
        selected.defaultMeterId ? Number(selected.defaultMeterId) : null
      );
      setDefaultUtilisation(() => {
        const util = selected.default_utilisation ?? selected.defaultUtilisation;
        return util !== undefined && util !== null ? Number(util) : null;
      });
    } else {
      setSerialRequired(false);
      setDefaultMeterId(null);
      setDefaultUtilisation(null);
    }
    prevAssetTypeId.current = newAsset.AssetTypeId;
  }, [assetTypes, newAsset.AssetTypeId]);

  const handleGroupSearch = async (inputValue) => {
    try {
      const shouldLoadDefault = !inputValue;
      if (!shouldLoadDefault && inputValue.length < 2) return [];
      const resp = await getPaginatedGlobalGroupsCombined({
        page: 1,
        pageSize: 10,
        search: shouldLoadDefault ? '' : inputValue,
      });
      const items = resp.data || resp;
      const options = items.map((g) => ({
        value: g.id,
        label: `${g.globalManufacturer?.manufacturer || g.manufacturer?.manufacturer || ''
          } ${g.groupName} ${g.globalClass?.className || g.class?.className || ''
          }`.trim(),
        isLocal: g.isLocal || false,
      }));
      return [
        { value: 'add-new', label: '+ Add New Model/Group', isLocal: true },
        ...options,
      ];
    } catch (err) {
      console.error('Error fetching groups:', err);
      return [{ value: 'add-new', label: '+ Add New Model/Group', isLocal: true }];
    }
  };

  useEffect(() => {
    if (!newAsset.groupId) return;
    (async () => {
      try {
        const grp =
          selectedGroup?.isLocal === false
            ? await getGlobalGroupById(newAsset.groupId)
            : await getGroupById(newAsset.groupId);
        const label = `${grp.globalManufacturer?.manufacturer || grp.manufacturer?.manufacturer || ''
          } ${grp.groupName || ''} ${grp.globalClass?.className || grp.class?.className || ''
          }`.trim();
        const categoryId =
          grp.globalClass?.categoryId ?? grp.class?.categoryId ?? null;
        setNewAsset((prev) => ({
          ...prev,
          assetDescription: label,
          assetImage: grp.groupImage || prev.assetImage,
          ...(showAssetCategory && { assetCategoryId: categoryId }),
        }));
        setSelectedGroup({
          value: grp.id,
          label,
          isLocal:
            selectedGroup?.isLocal === false
              ? false
              : grp.isLocal ?? !(grp.globalManufacturer || grp.globalClass || grp.globalGroupId),
        });
      } catch (err) {
        console.error('Error setting selected group', err);
      }
    })();
  }, [newAsset.groupId, selectedGroup?.isLocal, showAssetCategory]);

  useEffect(() => {
    if (!newAsset.assetStartDate || !newAsset.lifeInYears) return;
    const start = new Date(newAsset.assetStartDate);
    if (isNaN(start.getTime())) return;
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + Number(newAsset.lifeInYears));
    const dateOnly = end.toISOString().split('T')[0];
    if (newAsset.assetEndDate !== dateOnly) {
      setNewAsset((prev) => ({ ...prev, assetEndDate: dateOnly }));
    }
  }, [newAsset.assetStartDate, newAsset.lifeInYears]);

  const isBasicType = useMemo(() => {
    const desc = planningTypes.find((t) => t.id === newAsset.PlanningTypeId)?.typeDescription;
    return desc?.toLowerCase() === 'basic';
  }, [planningTypes, newAsset.PlanningTypeId]);

  const planningRequiresMeter = [1, 2].includes(newAsset.PlanningTypeId);
  const handleAddMeter = useCallback(
    (autoMeter) => {
      if (autoMeter) {
        setAddedMeters((prev) => {
          if (prev.some((m) => m.meterId === autoMeter.meterId)) return prev;
          const updatedPrev = autoMeter.primaryMeter
            ? prev.map((m) => ({ ...m, primaryMeter: 0 }))
            : prev;
          return [...updatedPrev, autoMeter];
        });
        return;
      }

      if (!validateMeterForm()) return;
      const selectedMeter = meterList.find((m) => m.id === newMeterId);
      if (selectedMeter?.rules) {
        try {
          const rule = Array.isArray(selectedMeter.rules)
            ? selectedMeter.rules[0]
            : selectedMeter.rules;
          const period = Number(rule.period) || 1;
          const util = Number(newUtilisation || 0);
          const factor = 365 / period;
          const maxAnnual =
            rule.maxValue !== undefined && rule.maxValue !== null
              ? Number(rule.maxValue) * factor
              : undefined;
          const minAnnual =
            rule.minValue !== undefined && rule.minValue !== null
              ? Number(rule.minValue) * factor
              : undefined;
          if (
            (maxAnnual !== undefined && util > maxAnnual) ||
            (minAnnual !== undefined && util < minAnnual)
          ) {
            if (maxAnnual !== undefined && util > maxAnnual) {
              setUtilisationError(
                `Utilisation must be less than or equal to ${maxAnnual}`
              );
            } else {
              setUtilisationError(
                `Utilisation must be greater than or equal to ${minAnnual}`
              );
            }
            return;
          }
        } catch (err) {
          console.error('Error validating utilisation', err);
        }
      }
      setUtilisationError('');
      setAddedMeters((prev) => {
        const isEditing = editingMeterIndex !== null && editingMeterIndex !== undefined;

        const meterData = {
          meterId: newMeterId,
          startReading: newStartReading || 0,
          utilisation: Number(newUtilisation) || 0,
          primaryMeter: newPrimaryMeter ? 1 : 0,
        };

        const hasDuplicate = prev.some((m, idx) => {
          if (isEditing && idx === editingMeterIndex) return false;
          return m.meterId === meterData.meterId;
        });
        if (hasDuplicate) {
          return prev;
        }

        const updatedPrev = newPrimaryMeter
          ? prev.map((m, idx) => (idx === editingMeterIndex ? m : { ...m, primaryMeter: 0 }))
          : [...prev];

        if (isEditing) {
          return updatedPrev.map((m, idx) => (idx === editingMeterIndex ? meterData : m));
        }

        return [...updatedPrev, meterData];
      });
      setShowAddMeterModal(false);
      resetMeterForm();
    },
    [
      newMeterId,
      newStartReading,
      newUtilisation,
      newPrimaryMeter,
      meterList,
      editingMeterIndex,
      resetMeterForm,
      validateMeterForm,
    ]
  );

  const handleDeleteMeter = useCallback((index) => {
    setAddedMeters((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  useEffect(() => {
    if (
      currentStep === 3 &&
      planningRequiresMeter &&
      defaultMeterId &&
      !addedMeters.some((m) => m.meterId === defaultMeterId)
    ) {
      handleAddMeter({
        meterId: defaultMeterId,
        startReading: 0,
        utilisation: Number(defaultUtilisation ?? 0),
        primaryMeter: 1,
      });
    }
  }, [
    currentStep,
    planningRequiresMeter,
    defaultMeterId,
    defaultUtilisation,
    addedMeters,
    handleAddMeter,
  ]);

  const assetTypeFields = useMemo(
    () => {
      const fields = [
        {
          field: 'groupId',
          label: 'Model or Group',
          type: 'paginated-select',
          size: 6,
          loadOptions: handleGroupSearch,
          selectedOption: selectedGroup,
          required: true,
          onChange: handleGroupChange,
        },
        {
          field: 'AssetTypeId',
          label: 'Asset Type',
          type: 'select',
          size: 6,
          api: 'asset-type',
          displayField: 'name',
          required: true,
          tooltip: 'Asset Type determines which fields are available for the asset.'
        },
        { field: 'PlanningTypeId', label: 'Planning Type', type: 'select', size: 6, api: 'planning-types', displayField: 'typeDescription', required: true, tooltip: 'Planning Type sets the downtime requirements for the asset. Advanced uses Events with complex utilisation, Standard used Events with basic utilisation and Basic does not use events.' },
      ];
      if (showAssetCategory) {
        fields.push({ field: 'assetCategoryId', label: 'Category', type: 'select', size: 6, api: 'categories', displayField: 'categoryName', required: false });
      }
      fields.push({ field: 'assetImage', label: 'Asset Image', type: 'image', size: 12, directory: 'modelimages' });
      return fields;
    },
    [selectedGroup, showAssetCategory]
  );

  const assetDetailFields = useMemo(
    () => [
      { field: 'assetDescription', label: 'Description', type: 'text', size: 12, required: true },
      { field: 'assetNumber', label: 'Asset Number', type: 'text', size: 6, required: true },
      { field: 'assetSerial', label: 'Serial Number', type: 'text', size: 6, required: serialRequired },
      { field: 'assetDepartmentId', label: 'Department', type: 'select', size: 6, api: 'hierarchy-structures', displayField: 'name', required: true },
      {
        field: 'costCenterId',
        label: 'Cost Centre',
        type: 'select',
        size: 6,
        options: costCentreOptions,
        required: true,
        onChange: handleCostCentreChange,
      },
      { field: 'plantStatusId', label: 'Status', type: 'select', size: 5, api: 'plant-status', displayField: 'status', required: true },
      { field: 'assetCriticalityId', label: 'Criticality', type: 'select', size: 5, api: 'asset-criticality', displayField: 'criticalityName', required: true, disabled: isBasicType },
      { field: 'buildYear', label: 'Build Year', type: 'text', size: 2, required: false },
      { field: 'notes', label: 'Notes', type: 'textarea', size: 12, required: false },
    ],
    [
      serialRequired,
      isBasicType,
      costCentreOptions,
      handleCostCentreChange,
    ]
  );

  const meterFields = useMemo(
    () => [
      {
        field: 'assetStartDate',
        label: 'Start Date',
        type: 'date',
        size: 4,
        required: true,
        noFutureDate: true,
        max: todayDate,
      },
      { field: 'assetEndDate', label: 'End Date', type: 'date', size: 4, required: true },
      {
        field: 'lifeInYears',
        label: 'Useful Life (years)',
        type: 'select',
        size: 4,
        options: [
          { value: 1, label: '1 Year' },
          { value: 2, label: '2 Years' },
          { value: 3, label: '3 Years' },
          { value: 4, label: '4 Years' },
          { value: 5, label: '5 Years' },
          { value: 6, label: '6 Years' },
          { value: 7, label: '7 Years' },
          { value: 8, label: '8 Years' },
          { value: 9, label: '9 Years' },
          { value: 10, label: '10 Years' },
          { value: 12, label: '12 Years' },
          { value: 15, label: '15 Years' },
          { value: 20, label: '20 Years' },
          { value: 25, label: '25 Years' },
        ],
        required: false,
      },
    ],
    [todayDate]
  );

  const {
    errors: lifeUtilisationErrors,
    setErrors: setLifeUtilisationErrors,
    validate: validateLifeUtilisation,
  } = useValidator(meterFields, newAsset);

  const combinedErrors = useMemo(
    () => ({ ...errors, ...lifeUtilisationErrors }),
    [errors, lifeUtilisationErrors],
  );

  /* const assetFinanceFields = useMemo(() => {
    const fields = [
      { field: 'purchasePrice', label: 'Purchase Price', type: 'number', size: 4, required: false, step: 1 },
      { field: 'depnRate', label: 'Depreciation Rate %', type: 'number', size: 4, required: false },
      {
        field: 'depnType',
        label: 'Depreciation Type',
        type: 'select',
        size: 4,
        options: [
          { value: 'straightline', label: 'Straightline' },
          { value: 'decliningbalance', label: 'Declining Balance' },
          { value: 'usagebased', label: 'Usage Based' },
          { value: 'none', label: 'None' },
        ],
        required: false,
      },
      { field: 'leased', label: 'Leased?', type: 'switch', size: 12, required: false },
    ];

    if (newAsset.leased) {
      fields.push(
        { field: 'financeCompany', label: 'Finance Company', type: 'text', size: 4, required: false },
        {
          field: 'leaseType',
          label: 'Lease Type',
          type: 'select',
          size: 4,
          options: [
            { value: 'finance', label: 'Finance' },
            { value: 'operating', label: 'Operating' },
            { value: 'none', label: 'None' },
          ],
          required: false,
        },
        { field: 'leaseInterestRate', label: 'Lease Interest Rate %', type: 'number', size: 4, required: false },
        { field: 'leaseStartDate', label: 'Lease Start Date', type: 'date', size: 4, required: false },
        { field: 'leaseEndDate', label: 'Lease End Date', type: 'date', size: 4, required: false },
        { field: 'leasePayments', label: 'Lease Payments', type: 'number', size: 4, required: false }
      );
    }

    return fields;
  }, [newAsset.leased]); */

  /* const financeFieldKeys = [
    'purchasePrice',
    'depnRate',
    'depnType',
    'leased',
    'financeCompany',
    'leaseType',
    'leaseInterestRate',
    'leaseStartDate',
    'leaseEndDate',
    'leasePayments',
    'endValue',
  ]; */

  const { renderField: renderAssetTypeField } = useFormBuilder(
    assetTypeFields,
    newAsset,
    setNewAsset,
    combinedErrors,
  );
  const { renderField: renderAssetDetailField } = useFormBuilder(
    assetDetailFields,
    newAsset,
    setNewAsset,
    combinedErrors,
  );
  const { renderField: renderMeterField } = useFormBuilder(
    meterFields,
    newAsset,
    setNewAsset,
    combinedErrors,
  );
  // const { renderField: renderFinanceField } = useFormBuilder(assetFinanceFields, newAsset, setNewAsset, errors);

  const validateStep = (fields) => {
    if (fields === meterFields) {
      return validateLifeUtilisation();
    }
    const errs = validateForm(fields, newAsset);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = async () => {
    let fields = [];
    if (currentStep === 1) fields = assetTypeFields;
    if (currentStep === 2) fields = assetDetailFields;
    if (currentStep === 3) fields = meterFields;
    if (!validateStep(fields)) return;

    if (
      currentStep === 2 &&
      [1, 2].includes(newAsset.PlanningTypeId) &&
      newAsset.assetSerial
    ) {
      try {
        const dup = await getAssetBySerial(newAsset.assetSerial);
        if (dup) {
          setErrors({ assetSerial: 'Serial Number already exists on another asset' });
          return;
        }
      } catch (err) {
        console.error('Error checking asset serial:', err);
      }
    }

    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    const fields = [...assetTypeFields, ...assetDetailFields, ...meterFields];
    const isLifeValid = validateLifeUtilisation();
    const errs = validateForm(fields, newAsset);
    if (newAsset.assetStartDate && newAsset.assetStartDate > todayDate) {
      errs.assetStartDate = 'Start Date cannot be in the future';
    }
    setErrors(errs);
    if (!isLifeValid || Object.keys(errs).length > 0) return;
    try {
      const cleanAssetPayload = {};
      // const financePayload = {};
      for (const key in newAsset) {
        if (newAsset[key] !== undefined && newAsset[key] !== '' && typeof newAsset[key] !== 'object') {
          cleanAssetPayload[key] = newAsset[key];
        }
      }
      const toDateOnly = (val) => {
        if (!val) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val).slice(0, 10);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };

      ['assetStartDate', 'assetEndDate', 'assetDisposalDate'].forEach((f) => {
        if (cleanAssetPayload[f]) cleanAssetPayload[f] = toDateOnly(cleanAssetPayload[f]);
      });

      const groupId = cleanAssetPayload.groupId;
      delete cleanAssetPayload.groupId;
      const assetPayloadBase = {
        ...cleanAssetPayload,
        organisationId: userProfile.org_id,
        assetFiles: newAsset.assetFiles,
        createdBy: userProfile.id,
      };
      if (selectedGroup?.isLocal) {
        assetPayloadBase.groupId = groupId;
      } else {
        assetPayloadBase.globalGroupId = groupId;
      }

      const created = await createAsset(assetPayloadBase);
      clearCache('groups');
      const assetId = created.id;
      /* if (Object.keys(financePayload).length > 0) {
        await createAssetFinancial({ ...financePayload, assetId });
      } */

      for (const m of addedMeters) {
        const meterEndOfLife = calculateMeterEndOfLife(newAsset, m.utilisation);
        await createAssetMeter({
          assetId,
          meterId: m.meterId,
          startReading: m.startReading || 0,
          utilisation: m.utilisation || 0,
          primaryMeter: m.primaryMeter ? 1 : 0,
          endOfLife: meterEndOfLife ?? 0,
        });
        if (newAsset.assetStartDate) {
          await createMeterReadings({
            assetId,
            metersId: m.meterId,
            value: parseFloat(m.startReading) || 0,
            readingDate: new Date(newAsset.assetStartDate).toISOString(),
          });
        }
      }

      onSaved && onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving asset:', err);
    }
  };

  if (!show) return null;

  return (
    <div className="modal modal-backdrop show d-block" tabIndex="-1">
      <div className="modal-dialog modal-xl" style={{ maxHeight: '90vh' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Asset</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="form-wizard">
              <form>
                <div className="form-wizard-header overflow-x-auto scroll-sm pb-8 my-32">
                  <ul className="list-unstyled form-wizard-list style-three">
                    <li className={`form-wizard-list__item d-flex align-items-center gap-8 ${[2, 3, 4].includes(currentStep) ? 'activated' : ''} ${currentStep === 1 ? 'active' : ''}`}> <div className='form-wizard-list__line'><span className='count'>1</span></div> <span className='text text-xs fw-semibold'>Asset Type</span> </li>
                    <li className={`form-wizard-list__item d-flex align-items-center gap-8 ${[3, 4].includes(currentStep) ? 'activated' : ''} ${currentStep === 2 ? 'active' : ''}`}> <div className='form-wizard-list__line'><span className='count'>2</span></div> <span className='text text-xs fw-semibold'>Asset Details</span> </li>
                    <li className={`form-wizard-list__item d-flex align-items-center gap-8 ${[4].includes(currentStep) ? 'activated' : ''} ${currentStep === 3 ? 'active' : ''}`}> <div className='form-wizard-list__line'><span className='count'>3</span></div> <span className='text text-xs fw-semibold'>Life & Utilisation</span> </li>
                    {/* <li className={`form-wizard-list__item d-flex align-items-center gap-8 ${currentStep === 4 ? 'active' : ''}`}> <div className='form-wizard-list__line'><span className='count'>4</span></div> <span className='text text-xs fw-semibold'>Financial Details</span> </li> */}
                  </ul>
                </div>
                <fieldset className={`wizard-fieldset ${currentStep === 1 ? 'show' : ''}`}> <h6 className='text-md text-neutral-500 mb-24'>Asset Type</h6><div className='row gy-3'>{assetTypeFields.map(renderAssetTypeField)}</div><div className='form-group text-end mt-16'><button onClick={nextStep} type='button' className='form-wizard-next-btn btn btn-primary-600 px-32'>Next</button></div></fieldset>
                <fieldset className={`wizard-fieldset ${currentStep === 2 ? 'show' : ''}`}> <h6 className='text-md text-neutral-500 mb-24'>Asset Details</h6><div className='row gy-3'>{assetDetailFields.map(renderAssetDetailField)}</div><div className='form-group d-flex align-items-center justify-content-end gap-8 mt-16'><button onClick={prevStep} type='button' className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'>Back</button><button onClick={nextStep} type='button' className='form-wizard-next-btn btn btn-primary-600 px-32'>Next</button></div></fieldset>
                <fieldset className={`wizard-fieldset ${currentStep === 3 ? 'show' : ''}`}>
                  <h6 className='text-md text-neutral-500'>Asset Life</h6>
                  <div className='row gy-'>{meterFields.map(renderMeterField)}</div>
                  {planningRequiresMeter && (
                    <>
                      <h6 className='text-md text-neutral-500 mt-24'>Life & Utilisation</h6>
                      {addedMeters.length > 0 && (
                        <>
                          <div className='table-responsive'>
                            <table className='table bordered-table table-sm'>
                              <thead>
                                <tr>
                                  <th>Meter</th>
                                  <th>Start Reading</th>
                                  <th>Utilisation</th>
                                  <th>Primary</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {addedMeters.map((m, idx) => (
                                  <tr key={idx}>
                                    <td className="text-primary fw-bold">{meterList.find((mt) => mt.id === m.meterId)?.name || m.meterId}</td>
                                    <td>{m.startReading}</td>
                                    <td>{m.utilisation}</td>
                                    <td>{m.primaryMeter ? 'Yes' : ''}</td>
                                    <td>
                                      <button
                                        className='edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6'
                                        type='button'
                                        onClick={() => {
                                          setEditingMeterIndex(idx);
                                          setNewMeterId(m.meterId);
                                          setNewStartReading(
                                            m.startReading !== undefined && m.startReading !== null
                                              ? String(m.startReading)
                                              : '0'
                                          );
                                          setNewUtilisation(
                                            m.utilisation !== undefined && m.utilisation !== null
                                              ? String(m.utilisation)
                                              : ''
                                          );
                                          setNewPrimaryMeter(Boolean(m.primaryMeter));
                                          setUtilisationError('');
                                          setShowAddMeterModal(true);
                                        }}
                                      >
                                        <Icon icon='ic:baseline-edit' />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                      <div className='text-start my-16'>
                        <button
                          type='button'
                          className='btn btn-primary'
                          onClick={() => {
                            resetMeterForm();
                            setNewPrimaryMeter(addedMeters.length === 0);
                            setShowAddMeterModal(true);
                          }}
                        >
                          {addedMeters.length > 0 ? '+ Add Another Meter' : 'Add Meter'}
                        </button>
                      </div>
                    </>
                  )}
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-16'>
                    <button onClick={prevStep} type='button' className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'>Back</button>
                    <button type='button' onClick={handleSubmit} className='form-wizard-submit btn btn-primary-600 px-32'>Add Asset</button>
                  </div>
                </fieldset>
                {/* <fieldset className={`wizard-fieldset ${currentStep === 4 ? 'show' : ''}`}> <h6 className='text-md text-neutral-500 mb-24'>Financial Details</h6><div className='row gy-3'>{assetFinanceFields.map(renderFinanceField)}</div><div className='form-group d-flex align-items-center justify-content-end gap-8 mt-16'><button onClick={prevStep} type='button' className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'>Back</button><button type='button' onClick={handleSubmit} className='form-wizard-submit btn btn-primary-600 px-32'>Publish</button></div></fieldset>*/}
              </form>
            </div>
          </div>
        </div>
      </div>

      {showAddGroupModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Model or Group</h5>
                <button className="btn-close" onClick={() => setShowAddGroupModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">{groupInputFields.map(renderGroupField)}</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAddGroupModal(false)}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleGroupSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddCostCentreModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Cost Centre</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowAddCostCentreModal(false);
                    setNewCostCentre({});
                    setCostCentreErrors({});
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">{costCentreFields.map(renderCostCentreField)}</div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddCostCentreModal(false);
                    setNewCostCentre({});
                    setCostCentreErrors({});
                  }}
                >
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleCostCentreSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddMeterModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Meter</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowAddMeterModal(false);
                    resetMeterForm();
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Meter *</label>
                  <select
                    className="form-select"
                    value={newMeterId || ''}
                    onChange={(e) => setNewMeterId(Number(e.target.value))}
                  >
                    <option value="" disabled>
                      Select Meter
                    </option>
                    {meterList.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  {addMeterErrors.meterId && (
                    <div className="text-danger small">{addMeterErrors.meterId}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Start Reading</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newStartReading}
                    onChange={(e) => setNewStartReading(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Utilisation per Year</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newUtilisation}
                    onChange={(e) => setNewUtilisation(e.target.value)}
                  />
                  {addMeterErrors.utilisation && (
                    <div className="text-danger small">{addMeterErrors.utilisation}</div>
                  )}
                  {utilisationError && (
                    <div className="text-danger small">{utilisationError}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Primary Meter</label>
                  <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={newPrimaryMeter}
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddMeterModal(false);
                    resetMeterForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAddMeter()}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAssetWizard;
